import React, { useState, useRef } from 'react';
import {
  Box, TextField, Button, MenuItem, Typography, Avatar,
  Divider, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import PreviewIcon from '@mui/icons-material/Preview';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'axios';
import { API_BASE_URL } from '../constant/constant';
import html2pdf from 'html2pdf.js';

const StudentForm = ({ onClose }) => {
  const [student, setStudent] = useState({
    name: '', dob: '', gender: '', class_name: '', roll_number: '',
    guardian_name: '', contact_number: '', address: '', remarks: ''
  });
  const [photo, setPhoto] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const previewRef = useRef(null);

  const handleSave = async () => {
    if (!student.name || !student.dob || !student.class_name || !photo) {
      alert("Please fill all required fields including photo");
      return;
    }
    setIsSaving(true);
    try {
      const formData = new FormData();
      Object.entries(student).forEach(([key, value]) => formData.append(key, value));
      formData.append('photo', photo);

      await axios.post(`${API_BASE_URL}/api/student`, formData);
      alert('Student added successfully');

      setStudent({
        name: '', dob: '', gender: '', class_name: '', roll_number: '',
        guardian_name: '', contact_number: '', address: '', remarks: ''
      });
      setPhoto(null);
    } catch (err) {
      console.error('Save failed:', err);
      alert('Failed to save student');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePrint = () => {
    const printContent = previewRef.current;
    const win = window.open('', '_blank');
    win.document.write(`
      <html>
        <head>
          <title>Print Student Preview</title>
          <style>
            body { font-family: Arial; padding: 20px; }
            img { display: block; margin: auto; width: 100px; height: 100px; border-radius: 50%; }
            .info { margin-bottom: 10px; }
          </style>
        </head>
        <body>${printContent.innerHTML}</body>
      </html>
    `);
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  const handleDownloadPDF = () => {
    const element = previewRef.current;
    html2pdf().from(element).set({
      margin: 10,
      filename: 'student-preview.pdf',
      html2canvas: { scale: 2 },
      jsPDF: { orientation: 'portrait', unit: 'mm', format: 'a4' }
    }).save();
  };

  return (
    <Box display="flex" flexDirection="column" gap={2} p={2}>
      <Typography variant="h5" align="center">Add Student</Typography>

      <TextField label="Name" value={student.name} onChange={(e) => setStudent({ ...student, name: e.target.value })} />
      <TextField type="date" label="DOB" InputLabelProps={{ shrink: true }} value={student.dob} onChange={(e) => setStudent({ ...student, dob: e.target.value })} />
      <TextField label="Class" value={student.class_name} onChange={(e) => setStudent({ ...student, class_name: e.target.value })} />
      <TextField select label="Gender" value={student.gender} onChange={(e) => setStudent({ ...student, gender: e.target.value })}>
        <MenuItem value="Male">Male</MenuItem>
        <MenuItem value="Female">Female</MenuItem>
        <MenuItem value="Other">Other</MenuItem>
      </TextField>
      <TextField label="Roll Number" value={student.roll_number} onChange={(e) => setStudent({ ...student, roll_number: e.target.value })} />
      <TextField label="Guardian Name" value={student.guardian_name} onChange={(e) => setStudent({ ...student, guardian_name: e.target.value })} />
      <TextField label="Contact Number" value={student.contact_number} onChange={(e) => setStudent({ ...student, contact_number: e.target.value })} />
      <TextField label="Address" multiline rows={2} value={student.address} onChange={(e) => setStudent({ ...student, address: e.target.value })} />
      <TextField label="Remarks" value={student.remarks} onChange={(e) => setStudent({ ...student, remarks: e.target.value })} />

      <Box display="flex" justifyContent="center" alignItems="center" gap={2} flexWrap="wrap" my={2}>
        <Avatar src={photo ? URL.createObjectURL(photo) : ''} sx={{ width: 60, height: 60 }} />
        <Button variant="outlined" component="label">
          Upload Photo
          <input hidden type="file" accept="image/*" onChange={(e) => setPhoto(e.target.files[0])} />
        </Button>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box display="flex" justifyContent="center" alignItems="center" gap={2} flexWrap="wrap">
        <Button variant="contained" onClick={handleSave} disabled={isSaving}>
          {isSaving ? "Saving..." : "Save"}
        </Button>
        <Button variant="outlined" startIcon={<PreviewIcon />} onClick={() => setPreviewOpen(true)}>
          Preview
        </Button>
      </Box>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Student Preview</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} ref={previewRef}>
            <Avatar src={photo ? URL.createObjectURL(photo) : ''} sx={{ width: 80, height: 80, alignSelf: 'center' }} />
            <Typography><strong>Name:</strong> {student.name}</Typography>
            <Typography><strong>DOB:</strong> {student.dob}</Typography>
            <Typography><strong>Class:</strong> {student.class_name}</Typography>
            <Typography><strong>Gender:</strong> {student.gender}</Typography>
            <Typography><strong>Roll Number:</strong> {student.roll_number}</Typography>
            <Typography><strong>Guardian:</strong> {student.guardian_name}</Typography>
            <Typography><strong>Contact:</strong> {student.contact_number}</Typography>
            <Typography><strong>Address:</strong> {student.address}</Typography>
            <Typography><strong>Remarks:</strong> {student.remarks}</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDownloadPDF} startIcon={<DownloadIcon />}>Download PDF</Button>
          <Button onClick={handlePrint} startIcon={<PrintIcon />}>Print</Button>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StudentForm;
